import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  RefreshCw, 
  ExternalLink, 
  MapPin, 
  Calendar, 
  TrendingUp, 
  TrendingDown, 
  Star,
  BarChart3,
  AlertCircle
} from 'lucide-react';
import { BusinessDetails } from '@/types/index';
import { GoogleReviewCard } from './GoogleReviewCard';
import { cn } from '@/lib/core/utils';
import { useGooglePlacesData } from '@/hooks/business/useGooglePlacesData';

interface GoogleReviewsTabProps {
  business: BusinessDetails;
}

export const GoogleReviewsTab: React.FC<GoogleReviewsTabProps> = ({ business }) => {
  const { data: googleData, loading, error, refetch, lastUpdated } = useGooglePlacesData(business.google_place_id);

  // Calculate growth metrics
  const getGrowthMetrics = () => {
    if (!googleData || !business.initial_google_rating || !business.initial_google_review_count) {
      return null;
    }

    const ratingChange = googleData.rating - business.initial_google_rating;
    const reviewsGrowth = googleData.userRatingCount - business.initial_google_review_count;
    const daysSinceBaseline = business.initial_google_data_captured_at 
      ? Math.floor((Date.now() - new Date(business.initial_google_data_captured_at).getTime()) / (1000 * 60 * 60 * 24))
      : 0;

    return {
      ratingChange,
      reviewsGrowth,
      daysSinceBaseline,
      avgReviewsPerDay: daysSinceBaseline > 0 ? reviewsGrowth / daysSinceBaseline : 0,
      ratingTrend: ratingChange > 0.1 ? 'up' : ratingChange < -0.1 ? 'down' : 'neutral'
    };
  };

  const growthMetrics = getGrowthMetrics();

  // Render star rating
  const renderStars = (rating: number, className?: string) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className={cn("flex items-center", className)}>
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            className={cn(
              "w-4 h-4",
              i < fullStars
                ? 'fill-yellow-400 text-yellow-400'
                : i === fullStars && hasHalfStar
                ? 'fill-yellow-200 text-yellow-400'
                : 'text-gray-300'
            )}
          />
        ))}
        <span className="ml-2 font-semibold text-lg">{rating.toFixed(1)}</span>
      </div>
    );
  };

  if (!business.google_place_id) {
    return (
      <div className="space-y-6">
        <Card className="border-orange-200 bg-orange-50/50">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-orange-600" />
              <CardTitle className="text-orange-900">Google Places Not Configured</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-orange-700 mb-4">
              This business doesn't have a Google Place ID configured. To view Google review data, you need to link this business to a Google Places entry.
            </p>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Configure Google Places integration in the business settings to track Google reviews and ratings.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Google Reviews</h2>
          <p className="text-gray-600">Live data from Google Business Profile</p>
        </div>
        <div className="flex items-center space-x-2">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refetch}
            disabled={loading}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Main Google Review Card */}
      <GoogleReviewCard 
        business={business} 
        showGrowthMetrics={true}
        className="border-2"
      />

      {/* Detailed Analytics Grid */}
      {googleData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Current Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center">
                <BarChart3 className="w-4 h-4 mr-2" />
                Current Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Rating</p>
                  {renderStars(googleData.rating)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Reviews</p>
                  <p className="text-2xl font-bold">{googleData.userRatingCount.toLocaleString()}</p>
                </div>
                {googleData.businessStatus && (
                  <div>
                    <p className="text-sm text-gray-500">Business Status</p>
                    <Badge variant={googleData.businessStatus === 'OPERATIONAL' ? 'default' : 'secondary'}>
                      {googleData.businessStatus}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Growth Tracking Card */}
          {growthMetrics && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  {growthMetrics.ratingTrend === 'up' ? (
                    <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
                  ) : growthMetrics.ratingTrend === 'down' ? (
                    <TrendingDown className="w-4 h-4 mr-2 text-red-600" />
                  ) : (
                    <BarChart3 className="w-4 h-4 mr-2 text-gray-600" />
                  )}
                  Growth Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Rating Change</span>
                    <span className={cn(
                      "font-medium",
                      growthMetrics.ratingChange > 0.1 ? "text-green-600" :
                      growthMetrics.ratingChange < -0.1 ? "text-red-600" : "text-gray-600"
                    )}>
                      {growthMetrics.ratingChange >= 0 ? '+' : ''}{growthMetrics.ratingChange.toFixed(1)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">New Reviews</span>
                    <span className="font-medium text-green-600">
                      +{growthMetrics.reviewsGrowth.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Reviews/Day</span>
                    <span className="font-medium">
                      {growthMetrics.avgReviewsPerDay.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Tracking Days</span>
                    <span className="font-medium">
                      {growthMetrics.daysSinceBaseline}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Baseline Information Card */}
          {business.initial_google_data_captured_at && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Baseline Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Captured Date</p>
                    <p className="font-medium">
                      {new Date(business.initial_google_data_captured_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  {business.initial_google_rating && (
                    <div>
                      <p className="text-sm text-gray-500">Initial Rating</p>
                      <p className="font-medium">{business.initial_google_rating.toFixed(1)}/5</p>
                    </div>
                  )}
                  {business.initial_google_review_count && (
                    <div>
                      <p className="text-sm text-gray-500">Initial Reviews</p>
                      <p className="font-medium">{business.initial_google_review_count.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      )}

      {/* Actions Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Google Business Profile Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" asChild>
              <a
                href={`https://maps.google.com/?q=place_id:${business.google_place_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View on Google Maps
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a
                href="https://business.google.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Google Business Profile
              </a>
            </Button>
          </div>
          
          <Alert className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Note:</strong> This displays read-only Google review data. 
              To respond to Google reviews, use the Google Business Profile dashboard directly.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

    </div>
  );
};

export default GoogleReviewsTab;